import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";

/**
 * Hook to detect and manage embed mode based on URL parameters
 *
 * IMPORTANT: When making changes to embed functionality, ensure docs/embed-functionality.md is updated
 * to reflect current behavior, scope, and configuration options.
 *
 * @returns {Object} embed mode configuration
 */
const useEmbedMode = () => {
  const location = useLocation();
  const workspace = useWorkspace();

  const embedConfig = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const embedParam = searchParams.get("embed");

    // Check if embed functionality is enabled for this workspace
    const embedFeatureFlag =
      workspace?.config?.featureFlags?.enableEmbedForIntranetPages;

    // Use array-based approach - if undefined/null, default to disabled
    const enabledPageTypes = Array.isArray(embedFeatureFlag)
      ? embedFeatureFlag
      : [];

    // Determine current page type based on URL path
    const { pathname } = location;
    let currentPageType = null;

    if (pathname.includes("/teams/") || pathname.includes("/team/")) {
      currentPageType = "Teams";
    } else if (pathname.includes("/tags/") && !pathname.includes("/explore/")) {
      currentPageType = "Tags";
    } else if (pathname.includes("/explore/tags/")) {
      currentPageType = "Explore Tags";
    } else if (pathname.includes("/explore/")) {
      currentPageType = "Explore";
    }

    // Check if embed is enabled for this page type
    const isEmbedEnabled =
      enabledPageTypes.includes("All") ||
      (currentPageType && enabledPageTypes.includes(currentPageType));

    if (!embedParam || !isEmbedEnabled) {
      return {
        isEmbedMode: false,
        isEmbedEnabled,
        hideHeader: false,
        hideSidebar: false,
        hideFooter: false,
      };
    }

    // Simplified: only support embed=true for full page embedding
    if (embedParam === "true" || embedParam === "1") {
      return {
        isEmbedMode: true,
        isEmbedEnabled,
        hideHeader: true,
        hideSidebar: true,
        hideFooter: false,
      };
    }

    // For any other embed parameter, treat as disabled
    return {
      isEmbedMode: false,
      isEmbedEnabled,
      hideHeader: false,
      hideSidebar: false,
      hideFooter: false,
    };
  }, [workspace?.config?.featureFlags?.enableEmbedForIntranetPages, location]);

  return embedConfig;
};

export default useEmbedMode;
